/*
  Payload-Helfer für Shiftplan-V2-Timetable-Aktionen.
*/

import { parseDateValue } from "../date/month";
import {
  buildDepartmentPayload,
  CUSTOM_DEPARTMENT_ROW_ID,
  getCustomDepartmentName,
} from "./departments";

const DEFAULT_START_TIME = "08:00:00";
const DEFAULT_END_TIME = "17:00:00";
const MILLISECONDS_PER_DAY = 86_400_000;

function pad(value) {
  return String(value).padStart(2, "0");
}

function toDateOnly(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function addDays(date, amount) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

function formatApiDateTime(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}:${pad(date.getSeconds())}`;
}

function formatApiDate(date) {
  return formatApiDateTime(date).slice(0, 10);
}

function parseTimeParts(value, fallback) {
  const raw = String(value || "").trim();
  const match = raw.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  const source = match ? raw : fallback;
  const safeMatch = source.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);

  return {
    hours: Number(safeMatch?.[1] || 0),
    minutes: Number(safeMatch?.[2] || 0),
    seconds: Number(safeMatch?.[3] || 0),
  };
}

function getTimeFromDateTime(value, fallback) {
  const parsed = parseDateValue(value);
  if (!parsed) return fallback;
  return `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;
}

function combineDateAndTime(date, timeValue) {
  const time = parseTimeParts(timeValue, DEFAULT_START_TIME);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.hours, time.minutes, time.seconds);
}

function getShiftDateOffset(shift) {
  const startDate = toDateOnly(shift?.start || shift?.dateKey);
  const endDate = toDateOnly(shift?.end || shift?.dateKey);
  if (!startDate || !endDate) return 0;

  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / MILLISECONDS_PER_DAY));
}

function getHolidayDateOffset(holiday) {
  const startDate = toDateOnly(holiday?.startDate);
  const endDate = toDateOnly(holiday?.endDate || holiday?.startDate);
  if (!startDate || !endDate) return 0;

  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / MILLISECONDS_PER_DAY));
}

function isUserHolidayEntry(entry) {
  return Boolean(entry?.holidayTypeId || entry?.userHolidayTypeId || entry?.entryType === "user_holiday");
}

function getBreakDateOffset(shift, breakItem) {
  const shiftStartDate = toDateOnly(shift?.start || shift?.dateKey);
  const breakStartDate = toDateOnly(breakItem?.start);
  if (!shiftStartDate || !breakStartDate) return 0;

  return Math.max(0, Math.round((breakStartDate.getTime() - shiftStartDate.getTime()) / MILLISECONDS_PER_DAY));
}

function buildBreakPayloadsForTargetDate(shift, targetDate) {
  return (Array.isArray(shift?.breaks) ? shift.breaks : [])
    .map((item) => {
      // Dauer-Pausen haben keine Lage im Tag — nur die Minutenzahl reist mit.
      if (item?.type === "duration") {
        const minutes = Number(item?.durationMinutes);
        if (!Number.isFinite(minutes) || minutes <= 0) return null;
        return {
          break_type: "duration",
          start_at: null,
          end_at: null,
          duration_minutes: Math.round(minutes),
        };
      }

      const startTime = item?.startTime || getTimeFromDateTime(item?.start, "12:00:00");
      const endTime = item?.endTime || getTimeFromDateTime(item?.end, "12:30:00");
      const offset = getBreakDateOffset(shift, item);
      const start = combineDateAndTime(addDays(targetDate, offset), startTime);
      let end = combineDateAndTime(addDays(targetDate, offset), endTime);
      if (end <= start) {
        end = combineDateAndTime(addDays(targetDate, offset + 1), endTime);
      }

      return {
        break_type: item?.type || "fixed",
        start_at: formatApiDateTime(start),
        end_at: formatApiDateTime(end),
        duration_minutes: item?.durationMinutes,
      };
    })
    .filter(Boolean);
}

function formatTimeLabelFromDateTimes(startAt, endAt, isAllDay) {
  if (isAllDay) return "Ganztag";

  const startTime = getTimeFromDateTime(startAt, "").slice(0, 5);
  const endTime = getTimeFromDateTime(endAt, "").slice(0, 5);
  return [startTime, endTime].filter(Boolean).join(" - ");
}

function normalizeDateKey(value) {
  const date = toDateOnly(value);
  return date ? formatApiDate(date) : "";
}

function getCellDateKey(cell) {
  return normalizeDateKey(cell?.day?.key || cell?.day?.date);
}

function getCellEmployeeName(cell) {
  return String(cell?.row?.name || cell?.row?.employeeName || "").trim();
}

function normalizeCellContexts(contexts) {
  const seenKeys = new Set();

  return (Array.isArray(contexts) ? contexts : [contexts])
    .filter((cell) => cell?.rowMode === "employee")
    .map((cell) => {
      const userId = getCellEmployeeId(cell);
      const dateKey = getCellDateKey(cell);
      if (!userId || !dateKey) return null;

      const key = `${userId}::${dateKey}`;
      if (seenKeys.has(key)) return null;
      seenKeys.add(key);

      return {
        cell,
        userId,
        employeeName: getCellEmployeeName(cell),
        dateKey,
        date: toDateOnly(dateKey),
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const userCompare = String(left.userId).localeCompare(String(right.userId), "de", { numeric: true });
      if (userCompare !== 0) return userCompare;
      return left.date.getTime() - right.date.getTime();
    });
}

function groupCellContextsByContiguousRanges(contexts) {
  return normalizeCellContexts(contexts).reduce((groups, item) => {
    const previousGroup = groups[groups.length - 1];
    const previousItem = previousGroup?.items?.[previousGroup.items.length - 1];
    const isSameEmployee = previousItem && String(previousItem.userId) === String(item.userId);
    const expectedNextDate =
      previousItem && formatApiDate(addDays(previousItem.date, 1)) === item.dateKey;

    if (isSameEmployee && expectedNextDate) {
      previousGroup.items.push(item);
      previousGroup.endDate = item.dateKey;
      return groups;
    }

    groups.push({
      userId: item.userId,
      employeeName: item.employeeName,
      startDate: item.dateKey,
      endDate: item.dateKey,
      items: [item],
    });
    return groups;
  }, []);
}

function normalizeQuickTimeOffOption(option) {
  const source = String(option?.source || option?.absenceKind || "absence").trim();
  const id = String(option?.id ?? option?.absenceId ?? option?.userHolidayTypeId ?? "").trim();
  const name = String(option?.name ?? option?.label ?? option?.title ?? "").trim() || "Absenz";
  const isUserHoliday = source === "user_holiday" || source === "holiday" || Boolean(option?.userHolidayTypeId);

  return {
    id,
    name,
    source: isUserHoliday ? "user_holiday" : "absence",
    key: option?.key || `${isUserHoliday ? "user_holiday" : "absence"}:${id || name}`,
  };
}

export function buildQuickTimeOffRange(contexts, anchorContext = null) {
  const groups = groupCellContextsByContiguousRanges(contexts);
  if (!groups.length) return null;

  const anchorUserId = getCellEmployeeId(anchorContext);
  const anchorDateKey = getCellDateKey(anchorContext);
  const anchoredGroup =
    anchorUserId && anchorDateKey
      ? groups.find(
          (group) =>
            String(group.userId) === String(anchorUserId) &&
            group.startDate <= anchorDateKey &&
            group.endDate >= anchorDateKey
        )
      : null;
  const group = anchoredGroup || groups[0];

  return {
    startDate: group.startDate,
    endDate: group.endDate,
  };
}

export function buildQuickTimeOffDrafts({ context, contexts, option } = {}) {
  const optionDetails = normalizeQuickTimeOffOption(option);
  if (!optionDetails.id && optionDetails.source === "user_holiday") return [];

  const sourceCells = Array.isArray(contexts) && contexts.length ? contexts : [context];
  return groupCellContextsByContiguousRanges(sourceCells).map((group) => ({
    entryType: "absence",
    employeeId: String(group.userId),
    employeeName: group.employeeName,
    absenceOptionKey: optionDetails.key,
    absenceKind: optionDetails.source,
    absenceId: optionDetails.source === "absence" ? optionDetails.id : "",
    userHolidayTypeId: optionDetails.source === "user_holiday" ? optionDetails.id : "",
    absenceType: optionDetails.name,
    absenceAllDay: true,
    absenceStartDate: group.startDate,
    absenceEndDate: group.endDate,
    absenceStartTime: "08:00",
    absenceEndTime: "17:00",
    absenceNote: "",
  }));
}

function buildResizedAbsenceDateTimes(entry, startDateKey, endDateKey) {
  const startDate = toDateOnly(startDateKey);
  const endDate = toDateOnly(endDateKey);
  if (!startDate || !endDate) return null;

  const isAllDay = Boolean(entry?.isAllDay);
  const startTime = isAllDay ? "00:00:00" : getTimeFromDateTime(entry?.start, DEFAULT_START_TIME);
  const endTime = isAllDay ? "23:59:59" : getTimeFromDateTime(entry?.end, DEFAULT_END_TIME);
  const start = combineDateAndTime(startDate, startTime);
  let end = combineDateAndTime(endDate, endTime);

  if (end <= start) {
    end = combineDateAndTime(addDays(startDate, 1), endTime);
  }

  return {
    startAt: formatApiDateTime(start),
    endAt: formatApiDateTime(end),
    isAllDay,
  };
}

function buildOptimisticBreaksFromPayloads(breaksPayload, entryId) {
  return (Array.isArray(breaksPayload) ? breaksPayload : []).map((item, index) => {
    const type = item?.break_type || "fixed";
    return {
      id: `${entryId}-break-${index}`,
      type,
      start: item?.start_at || "",
      end: item?.end_at || "",
      // Dauer-Pausen tragen keine Zeiten — die 12:00-Vorgabe wuerde ihnen
      // sonst ein erfundenes Zeitfenster andichten.
      startTime: type === "duration" ? "" : getTimeFromDateTime(item?.start_at, "12:00:00").slice(0, 5),
      endTime: type === "duration" ? "" : getTimeFromDateTime(item?.end_at, "12:30:00").slice(0, 5),
      durationMinutes: item?.duration_minutes == null ? null : Number(item.duration_minutes),
    };
  });
}

function normalizeApiId(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (!/^\d+$/.test(raw)) return "";
  const numericId = Number(raw);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : raw;
}

function normalizeChangeApiId(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) {
    const numericId = Number(raw);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : "";
  }
  return /^\d+-2$/.test(raw) ? raw : "";
}

function getCellEmployeeId(cell) {
  const id = Number(cell?.row?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function getMoveTarget(cell, shift) {
  if (!cell || !shift) return null;

  const customDepartment = getCustomDepartmentName(shift);

  if (cell.rowMode === "employee") {
    const userId = getCellEmployeeId(cell);
    if (!userId) return null;
    if (customDepartment) return { userId, department: customDepartment, isCustomDepartment: true };
    return {
      userId,
      department: shift.entryType === "custom_shift" ? shift.department || "" : shift.department || shift.title || "Schicht",
      isCustomDepartment: false,
    };
  }

  if (cell.rowMode === "department") {
    const userId = Number(shift.employeeId);
    if (!Number.isFinite(userId) || userId <= 0) return null;

    /*
      Die Zeile "Individuell" ist eine Sammelzeile, kein Ziel mit eigenem Namen.
      Ein Eintrag, der dort hineingezogen wird, behält seinen eigenen Namen;
      einer ohne bleibt liegen, weil sonst ein leerer Name entstünde.
    */
    if (String(cell?.row?.id || "") === CUSTOM_DEPARTMENT_ROW_ID) {
      if (!customDepartment) return null;
      return { userId, department: customDepartment, isCustomDepartment: true };
    }

    const department = cell?.row?.isUnassigned ? "" : String(cell?.row?.name || cell?.row?.id || "").trim();
    if (!department && shift.entryType !== "custom_shift") return null;
    return { userId, department, isCustomDepartment: false };
  }

  return null;
}

function getTargetEmployeeName(cell, shift) {
  if (cell?.rowMode === "employee") {
    const row = cell?.row;
    const name = [row?.firstName, row?.lastName].filter(Boolean).join(" ").trim();
    return row?.name || name || shift?.employeeName || "";
  }

  return shift?.employeeName || "";
}

function isSameMoveTarget(shift, cell, target) {
  if (!shift || !cell || !target) return false;
  const sameDate = String(shift.dateKey || "") === String(cell?.day?.key || "");

  if (cell.rowMode === "employee") {
    return sameDate && String(shift.employeeId || "") === String(target.userId);
  }

  return sameDate && String(shift.department || "") === String(target.department || "");
}

function buildMovedBreaks(shift, targetDate) {
  return (Array.isArray(shift?.breaks) ? shift.breaks : []).map((item) => {
    // Dauer-Pausen haengen an keinem Datum — beim Verschieben bleibt nur die
    // Minutenzahl.
    if (item?.type === "duration") {
      return { ...item, start: "", end: "", startTime: "", endTime: "" };
    }

    const startTime = item?.startTime || getTimeFromDateTime(item?.start, "12:00:00");
    const endTime = item?.endTime || getTimeFromDateTime(item?.end, "12:30:00");
    const offset = getBreakDateOffset(shift, item);
    const start = combineDateAndTime(addDays(targetDate, offset), startTime);
    let end = combineDateAndTime(addDays(targetDate, offset), endTime);
    if (end <= start) {
      end = combineDateAndTime(addDays(targetDate, offset + 1), endTime);
    }

    return {
      ...item,
      start: formatApiDateTime(start),
      end: formatApiDateTime(end),
      startTime,
      endTime,
    };
  });
}

export function isReleasableDraftShift(shift) {
  if (!shift || shift.entryType === "absence" || !shift.isDraft) return false;
  return Boolean(normalizeChangeApiId(shift.id));
}

export function buildReleaseEventFromShift(shift) {
  if (!isReleasableDraftShift(shift)) return null;

  const id = normalizeChangeApiId(shift.id);
  const userId = Number(shift.employeeId);
  const start = parseDateValue(shift.start);
  const end = parseDateValue(shift.end);
  const targetDate = toDateOnly(shift.start || shift.dateKey);
  if (!id || !Number.isFinite(userId) || userId <= 0 || !start || !end || !targetDate) return null;

  const entryType = shift.entryType === "custom_shift" ? "custom_shift" : "shift";

  return {
    id,
    action: "release",
    user_id: userId,
    ...buildDepartmentPayload(
      getCustomDepartmentName(shift) ||
        (entryType === "custom_shift" ? shift.department : shift.department || shift.title || "Schicht"),
      Boolean(getCustomDepartmentName(shift))
    ),
    custom_shift_name: entryType === "custom_shift" ? shift.title || shift.department || "Individuelle Schicht" : null,
    note: shift.note || "",
    start_at: formatApiDateTime(start),
    end_at: formatApiDateTime(end),
    is_all_day: false,
    shift_id: entryType === "shift" ? shift.shiftId || null : null,
    entry_type: entryType,
    absence_id: null,
    breaks: buildBreakPayloadsForTargetDate(shift, targetDate),
    release_status: "released",
  };
}

export function buildReleaseEventsFromShifts(shifts) {
  const seenIds = new Set();

  return (Array.isArray(shifts) ? shifts : []).reduce((events, shift) => {
    const event = buildReleaseEventFromShift(shift);
    if (!event || seenIds.has(String(event.id))) return events;
    seenIds.add(String(event.id));
    events.push(event);
    return events;
  }, []);
}

function buildCopiedEventRecords(entry, cells, options = {}) {
  if (!entry || !Array.isArray(cells) || !cells.length) return [];

  const copyBatchId = options.copyBatchId ?? Date.now();
  const idOffset = Number(options.idOffset || 0);

  if (isUserHolidayEntry(entry)) {
    const holidayTypeId = normalizeTemplateId(entry.holidayTypeId || entry.userHolidayTypeId);
    const endDateOffset = getHolidayDateOffset(entry);

    if (!holidayTypeId) return [];

    return cells
      .map((cell, index) => {
        const userId = getCellEmployeeId(cell);
        const targetDate = toDateOnly(cell?.day?.key);
        if (!userId || !targetDate) return null;

        return {
          cell,
          sourceEntry: entry,
          event: {
            id: `shiftplan-v2-copy-holiday-${copyBatchId}-${idOffset + index}`,
            action: "create",
            user_id: userId,
            start_date: formatApiDate(targetDate),
            end_date: formatApiDate(addDays(targetDate, endDateOffset)),
            entry_type: "user_holiday",
            user_holiday_type_id: holidayTypeId,
            // Ein abweichender Wert wird mitkopiert: kopiert wird, was man sieht.
            hours_per_day: normalizeHoursValue(entry.hoursPerDay),
          },
        };
      })
      .filter(Boolean);
  }

  const startTime = getTimeFromDateTime(entry.start, DEFAULT_START_TIME);
  const endTime = getTimeFromDateTime(entry.end, DEFAULT_END_TIME);
  const endDateOffset = getShiftDateOffset(entry);
  const entryType = entry.entryType === "absence" ? "absence" : entry.shiftId ? "shift" : "custom_shift";
  const subject = entryType === "absence" ? entry.title || entry.department || "Absenz" : entry.department || entry.title || "Schicht";

  return cells
    .map((cell, index) => {
      const userId = getCellEmployeeId(cell);
      const targetDate = toDateOnly(cell?.day?.key);
      if (!userId || !targetDate) return null;

      const start = combineDateAndTime(targetDate, startTime);
      let end = combineDateAndTime(addDays(targetDate, endDateOffset), endTime);
      if (end <= start) {
        end = combineDateAndTime(addDays(targetDate, endDateOffset + 1), endTime);
      }

      return {
        cell,
        sourceEntry: entry,
        event: {
          id: `shiftplan-v2-copy-${copyBatchId}-${idOffset + index}`,
          action: "create",
          user_id: userId,
          ...buildDepartmentPayload(
            getCustomDepartmentName(entry) ||
              (entryType === "absence" || entryType === "custom_shift" ? entry.department : subject),
            Boolean(getCustomDepartmentName(entry))
          ),
          custom_shift_name: entryType === "custom_shift" ? entry.title || subject : null,
          note: entry.note || "",
          start_at: formatApiDateTime(start),
          end_at: formatApiDateTime(end),
          is_all_day: Boolean(entry.isAllDay),
          shift_id: entryType === "shift" ? entry.shiftId || null : null,
          entry_type: entryType,
          absence_id: entryType === "absence" ? entry.absenceId || null : null,
          absence_hours_per_day:
            entryType === "absence" && entry.isAllDay ? normalizeHoursValue(entry.absenceHoursPerDay) : null,
          release_status: entryType === "absence" ? "released" : "draft",
          breaks: entryType === "absence" ? [] : buildBreakPayloadsForTargetDate(entry, targetDate),
        },
      };
    })
    .filter(Boolean);
}

function normalizeCopiedEntryList(entryOrEntries) {
  const entries = Array.isArray(entryOrEntries) ? entryOrEntries : [entryOrEntries];
  const seenIds = new Set();

  return entries.filter((entry) => {
    const id = String(entry?.id || "").trim();
    if (!entry || !id || seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });
}

function getEntryDateOnly(entry) {
  return toDateOnly(entry?.start || entry?.startDate || entry?.dateKey);
}

function getDateOffsetFromAnchor(anchorDate, entry) {
  const entryDate = getEntryDateOnly(entry);
  if (!anchorDate || !entryDate) return 0;
  return Math.round((entryDate.getTime() - anchorDate.getTime()) / MILLISECONDS_PER_DAY);
}

function buildCellWithDateOffset(cell, offset) {
  const baseDate = toDateOnly(cell?.day?.key);
  if (!baseDate) return null;

  const targetDate = addDays(baseDate, offset);
  return {
    ...cell,
    day: {
      ...(cell?.day || {}),
      key: formatApiDate(targetDate),
      date: targetDate,
    },
  };
}

function buildCopiedEventRecordsForEntries(entryOrEntries, cells) {
  const entries = normalizeCopiedEntryList(entryOrEntries);
  if (!entries.length) return [];
  if (entries.length === 1) return buildCopiedEventRecords(entries[0], cells);

  const copyBatchId = Date.now();
  const anchorDate = getEntryDateOnly(entries[0]);
  const cellCount = Array.isArray(cells) ? cells.length : 0;

  return entries.flatMap((entry, entryIndex) => {
    const dateOffset = getDateOffsetFromAnchor(anchorDate, entry);
    const targetCells = (Array.isArray(cells) ? cells : [])
      .map((cell) => buildCellWithDateOffset(cell, dateOffset))
      .filter(Boolean);

    return buildCopiedEventRecords(entry, targetCells, {
      copyBatchId,
      idOffset: entryIndex * Math.max(1, cellCount),
    });
  });
}

export function buildCreateEventsFromCopiedEntry(entry, cells) {
  return buildCopiedEventRecordsForEntries(entry, cells).map((record) => record.event);
}

export function buildOptimisticCopiedEntries(entry, cells) {
  const records = buildCopiedEventRecordsForEntries(entry, cells);

  return records.reduce(
    (accumulator, { event, cell, sourceEntry }) => {
      const source = sourceEntry || entry;

      if (event.entry_type === "user_holiday") {
        accumulator.userHolidays.push({
          ...source,
          id: event.id,
          userId: String(event.user_id),
          startDate: event.start_date,
          endDate: event.end_date,
          entryType: "user_holiday",
          created: "",
          released: "",
        });
        return accumulator;
      }

      const entryType = event.entry_type || source.entryType || "shift";
      const isAllDay = Boolean(event.is_all_day);
      const customDepartment = event.custom_department_name || "";
      const department =
        customDepartment ||
        (entryType === "custom_shift"
          ? source.department || ""
          : event.department_name || source.department || source.title || "");
      const title =
        entryType === "custom_shift"
          ? event.custom_shift_name || source.title || department || "Individuelle Schicht"
          : source.title || department || (entryType === "absence" ? "Absenz" : "Schicht");

      accumulator.shifts.push({
        ...source,
        id: event.id,
        employeeId: String(event.user_id),
        employeeName: getTargetEmployeeName(cell, source),
        entryType,
        absenceId: event.absence_id == null ? "" : String(event.absence_id),
        absenceHoursPerDay: event.absence_hours_per_day ?? null,
        department,
        customDepartmentName: customDepartment,
        title,
        dateKey: String(event.start_at || "").slice(0, 10),
        start: event.start_at,
        end: event.end_at,
        timeLabel: formatTimeLabelFromDateTimes(event.start_at, event.end_at, isAllDay),
        shiftId: event.shift_id == null ? "" : String(event.shift_id),
        note: event.note || "",
        releaseStatus: event.release_status || "",
        isDraft: entryType === "absence" ? false : true,
        isAllDay,
        breaks: buildOptimisticBreaksFromPayloads(event.breaks, event.id),
        created: "",
        released: "",
      });

      return accumulator;
    },
    { shifts: [], userHolidays: [] }
  );
}

export function buildDeleteEventFromEntry(entry) {
  if (!entry) return null;
  const id = normalizeApiId(entry.id);
  if (!id) return null;

  if (isUserHolidayEntry(entry)) {
    const userId = Number(entry.userId ?? entry.employeeId);
    if (!Number.isFinite(userId) || userId <= 0) return null;

    return {
      id,
      action: "delete",
      user_id: userId,
      entry_type: "user_holiday",
      start_date: entry.startDate || entry.dateKey || null,
      end_date: entry.endDate || entry.startDate || entry.dateKey || null,
    };
  }

  const userId = Number(entry.employeeId);
  if (!Number.isFinite(userId) || userId <= 0) return null;

  const start = parseDateValue(entry.start);
  const end = parseDateValue(entry.end);
  if (!start || !end) return null;

  return {
    id,
    action: "delete",
    user_id: userId,
    ...buildDepartmentPayload(
      getCustomDepartmentName(entry) || entry.department || entry.title || "Schicht",
      Boolean(getCustomDepartmentName(entry))
    ),
    note: entry.note || "",
    start_at: formatApiDateTime(start),
    end_at: formatApiDateTime(end),
    is_all_day: Boolean(entry.isAllDay),
    shift_id: entry.shiftId || null,
    entry_type: entry.entryType || "shift",
    absence_id: entry.absenceId || null,
  };
}

export function buildMoveEventFromShift(shift, targetCell) {
  if (!shift || !targetCell) return null;
  const id = normalizeChangeApiId(shift.id);
  if (!id) return null;

  const target = getMoveTarget(targetCell, shift);
  const targetDate = toDateOnly(targetCell?.day?.key);
  if (!target || !targetDate || isSameMoveTarget(shift, targetCell, target)) return null;

  const startTime = getTimeFromDateTime(shift.start, DEFAULT_START_TIME);
  const endTime = getTimeFromDateTime(shift.end, DEFAULT_END_TIME);
  const endDateOffset = getShiftDateOffset(shift);
  const start = combineDateAndTime(targetDate, startTime);
  let end = combineDateAndTime(addDays(targetDate, endDateOffset), endTime);
  if (end <= start) {
    end = combineDateAndTime(addDays(targetDate, endDateOffset + 1), endTime);
  }

  return {
    id,
    action: "change",
    user_id: target.userId,
    ...buildDepartmentPayload(target.department, target.isCustomDepartment),
    custom_shift_name: shift.entryType === "custom_shift" ? shift.title || target.department : null,
    note: shift.note || "",
    start_at: formatApiDateTime(start),
    end_at: formatApiDateTime(end),
    is_all_day: Boolean(shift.isAllDay),
    shift_id: shift.shiftId || null,
    entry_type: shift.entryType || "shift",
    absence_id: shift.absenceId || null,
    absence_hours_per_day:
      shift.entryType === "absence" && shift.isAllDay ? normalizeHoursValue(shift.absenceHoursPerDay) : null,
    release_status: shift.entryType === "absence" ? "released" : "draft",
    breaks: shift.entryType === "absence" ? [] : buildBreakPayloadsForTargetDate(shift, targetDate),
  };
}

export function buildOptimisticMovedShift(shift, targetCell) {
  if (!shift || !targetCell) return null;

  const target = getMoveTarget(targetCell, shift);
  const targetDate = toDateOnly(targetCell?.day?.key);
  if (!target || !targetDate || isSameMoveTarget(shift, targetCell, target)) return null;

  const startTime = getTimeFromDateTime(shift.start, DEFAULT_START_TIME);
  const endTime = getTimeFromDateTime(shift.end, DEFAULT_END_TIME);
  const endDateOffset = getShiftDateOffset(shift);
  const start = combineDateAndTime(targetDate, startTime);
  let end = combineDateAndTime(addDays(targetDate, endDateOffset), endTime);
  if (end <= start) {
    end = combineDateAndTime(addDays(targetDate, endDateOffset + 1), endTime);
  }

  return {
    ...shift,
    employeeId: String(target.userId),
    employeeName: getTargetEmployeeName(targetCell, shift),
    department: target.department,
    customDepartmentName: target.isCustomDepartment ? target.department : "",
    dateKey: targetCell.day.key,
    start: formatApiDateTime(start),
    end: formatApiDateTime(end),
    breaks: shift.entryType === "absence" ? [] : buildMovedBreaks(shift, targetDate),
    isDraft: shift.entryType === "absence" ? shift.isDraft : true,
  };
}

export function applyLocalShiftMoves(shifts, movedShifts) {
  const movesById = new Map(
    (Array.isArray(movedShifts) ? movedShifts : [])
      .filter((shift) => shift?.id != null)
      .map((shift) => [String(shift.id), shift])
  );
  if (!movesById.size) return Array.isArray(shifts) ? shifts : [];
  return (Array.isArray(shifts) ? shifts : []).map(
    (shift) => movesById.get(String(shift?.id)) || shift
  );
}

export function buildResizeEventFromEntry(entry, range) {
  if (!entry || !range) return null;

  const startDate = normalizeDateKey(range.startDate);
  const endDate = normalizeDateKey(range.endDate || range.startDate);
  if (!startDate || !endDate) return null;

  if (isUserHolidayEntry(entry)) {
    const id = normalizeChangeApiId(entry.id);
    const userId = Number(entry.userId ?? entry.employeeId);
    const userHolidayTypeId = normalizeTemplateId(entry.holidayTypeId || entry.userHolidayTypeId);
    if (!id || !Number.isFinite(userId) || userId <= 0 || !userHolidayTypeId) return null;

    return {
      id,
      action: "change",
      user_id: userId,
      start_date: startDate,
      end_date: endDate,
      entry_type: "user_holiday",
      user_holiday_type_id: userHolidayTypeId,
      // Ohne diesen Wert würde das Backend beim Ändern wieder den Wert des
      // Typs einsetzen und einen abweichenden Wert stillschweigend verwerfen.
      hours_per_day: normalizeHoursValue(entry.hoursPerDay),
    };
  }

  if (entry.entryType !== "absence") return null;

  const id = normalizeChangeApiId(entry.id);
  const userId = Number(entry.employeeId);
  const resizedDateTimes = buildResizedAbsenceDateTimes(entry, startDate, endDate);
  if (!id || !Number.isFinite(userId) || userId <= 0 || !resizedDateTimes) return null;

  return {
    id,
    action: "change",
    user_id: userId,
    note: entry.note || "",
    start_at: resizedDateTimes.startAt,
    end_at: resizedDateTimes.endAt,
    is_all_day: resizedDateTimes.isAllDay,
    shift_id: null,
    entry_type: "absence",
    absence_id: entry.absenceId || null,
    absence_hours_per_day: resizedDateTimes.isAllDay
      ? normalizeHoursValue(entry.absenceHoursPerDay)
      : null,
    release_status: "released",
  };
}

export function buildOptimisticResizedEntry(entry, range) {
  if (!entry || !range) return null;

  const startDate = normalizeDateKey(range.startDate);
  const endDate = normalizeDateKey(range.endDate || range.startDate);
  if (!startDate || !endDate) return null;

  if (isUserHolidayEntry(entry)) {
    return {
      ...entry,
      startDate,
      endDate,
      entryType: "user_holiday",
    };
  }

  if (entry.entryType !== "absence") return null;

  const resizedDateTimes = buildResizedAbsenceDateTimes(entry, startDate, endDate);
  if (!resizedDateTimes) return null;

  return {
    ...entry,
    dateKey: startDate,
    start: resizedDateTimes.startAt,
    end: resizedDateTimes.endAt,
    timeLabel: formatTimeLabelFromDateTimes(
      resizedDateTimes.startAt,
      resizedDateTimes.endAt,
      resizedDateTimes.isAllDay
    ),
    breaks: [],
    isAllDay: resizedDateTimes.isAllDay,
  };
}

function normalizeTemplateId(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw.startsWith("__")) return null;
  return raw;
}

export function buildCreateEventFromDemandDrop(demandEntry, targetCell) {
  if (!demandEntry || targetCell?.rowMode !== "employee") return null;

  const userId = getCellEmployeeId(targetCell);
  const targetDate = toDateOnly(targetCell?.day?.key);
  const templateId = normalizeTemplateId(demandEntry.templateId);
  if (!userId || !targetDate || !templateId) return null;

  // Bedarf gilt pro Tag: Drop nur auf Zellen desselben Datums.
  if (String(targetCell?.day?.key || "").slice(0, 10) !== String(demandEntry.dateKey || "").slice(0, 10)) {
    return null;
  }

  const start = combineDateAndTime(targetDate, demandEntry.startTime || DEFAULT_START_TIME);
  let end = combineDateAndTime(targetDate, demandEntry.endTime || DEFAULT_END_TIME);
  if (end <= start) {
    end = combineDateAndTime(addDays(targetDate, 1), demandEntry.endTime || DEFAULT_END_TIME);
  }

  return {
    id: `shiftplan-v2-demand-${demandEntry.demandId || demandEntry.id || templateId}-${userId}`,
    action: "create",
    user_id: userId,
    department_name: demandEntry.departmentName || null,
    note: "",
    start_at: formatApiDateTime(start),
    end_at: formatApiDateTime(end),
    is_all_day: false,
    shift_id: templateId,
    entry_type: "shift",
    breaks: [],
    release_status: "draft",
  };
}

function combineIsoDateAndTime(dateKey, timeValue, fallback) {
  const date = toDateOnly(dateKey);
  if (!date) return null;
  return combineDateAndTime(date, timeValue || fallback);
}

function formatDraftDateTime(dateKey, timeValue, fallback) {
  const date = combineIsoDateAndTime(dateKey, timeValue, fallback);
  return date ? formatApiDateTime(date) : "";
}

/*
  Stundenwert für die API: leer bleibt null, damit das Backend "kein fester
  Wert" von "null Stunden" unterscheiden kann.
*/
export function normalizeHoursValue(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(String(value).replace(",", "."));
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 24) return null;
  return Math.round(parsed * 100) / 100;
}

function buildEditorBreakPayload(draft) {
  const startDate = draft?.startDate;
  return (Array.isArray(draft?.breaks) ? draft.breaks : [])
    .map((item) => {
      // Dauer-Pausen (aus den Schicht-Stammdaten) haben keine Lage im Tag —
      // nur die Minutenzahl reist mit.
      if (item?.type === "duration") {
        const minutes = Number(item?.durationMinutes);
        if (!Number.isFinite(minutes) || minutes <= 0) return null;
        return {
          break_type: "duration",
          start_at: null,
          end_at: null,
          duration_minutes: Math.round(minutes),
        };
      }

      const start = combineIsoDateAndTime(startDate, item?.startTime, "12:00:00");
      const end = combineIsoDateAndTime(startDate, item?.endTime, "12:30:00");
      if (!start || !end || end < start) return null;
      return {
        break_type: "fixed",
        start_at: formatApiDateTime(start),
        end_at: formatApiDateTime(end),
      };
    })
    .filter(Boolean);
}

export function buildSaveEventFromEditorDraft({ context, draft }) {
  if (!draft) return null;

  const userId = Number(draft.employeeId);
  if (!Number.isFinite(userId) || userId <= 0) return null;

  const isEdit = context?.action === "edit";
  const id = isEdit ? normalizeChangeApiId(context?.shift?.id ?? context?.userHoliday?.id) : "";
  const actionType = isEdit && id ? "Change" : "Create";
  const entryType = draft.entryType === "absence" ? "absence" : "shift";

  if (entryType === "absence") {
    if (draft.absenceKind === "user_holiday") {
      const userHolidayTypeId = normalizeTemplateId(draft.userHolidayTypeId);
      if (!userHolidayTypeId || !draft.absenceStartDate || !draft.absenceEndDate) return null;

      return {
        id: id || `shiftplan-v2-editor-${Date.now()}`,
        action: actionType.toLowerCase(),
        user_id: userId,
        start_date: draft.absenceStartDate,
        end_date: draft.absenceEndDate,
        entry_type: "user_holiday",
        user_holiday_type_id: userHolidayTypeId,
        hours_per_day: normalizeHoursValue(draft.hoursPerDay),
      };
    }

    const startTime = draft.absenceAllDay ? "00:00:00" : draft.absenceStartTime || DEFAULT_START_TIME;
    const endTime = draft.absenceAllDay ? "23:59:59" : draft.absenceEndTime || DEFAULT_END_TIME;

    return {
      id: id || `shiftplan-v2-editor-${Date.now()}`,
      action: actionType.toLowerCase(),
      user_id: userId,
      note: draft.absenceNote || "",
      start_at: formatDraftDateTime(draft.absenceStartDate, startTime, "00:00:00"),
      end_at: formatDraftDateTime(draft.absenceEndDate, endTime, "23:59:59"),
      is_all_day: Boolean(draft.absenceAllDay),
      shift_id: null,
      entry_type: "absence",
      absence_id: draft.absenceId || context?.shift?.absenceId || null,
      // Nur bei ganztägig gefüllt — mit Uhrzeit rechnet die Uhrzeit.
      absence_hours_per_day: draft.absenceAllDay ? normalizeHoursValue(draft.hoursPerDay) : null,
      release_status: "released",
    };
  }

  const shiftId = normalizeTemplateId(draft.templateId);
  const isCustomShift = !shiftId;
  return {
    id: id || `shiftplan-v2-editor-${Date.now()}`,
    action: actionType.toLowerCase(),
    user_id: userId,
    ...buildDepartmentPayload(
      draft.department || (draft.isCustomDepartment ? "" : isCustomShift ? "" : draft.title || "Schicht"),
      Boolean(draft.isCustomDepartment)
    ),
    custom_shift_name: isCustomShift ? draft.title || draft.department || "Individuelle Schicht" : null,
    note: draft.note || "",
    start_at: formatDraftDateTime(draft.startDate, draft.startTime, DEFAULT_START_TIME),
    end_at: formatDraftDateTime(draft.endDate, draft.endTime, DEFAULT_END_TIME),
    is_all_day: false,
    shift_id: shiftId,
    entry_type: isCustomShift ? "custom_shift" : "shift",
    breaks: buildEditorBreakPayload(draft),
    release_status: "draft",
  };
}
